import { ChatRow } from 'components/chats/ChatRow'
import { useSelector } from 'react-redux'
import { settingsSelectors } from 'store/settings'
import { messagesActions, messagesSelectors } from 'store/messages'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { chain, isEmpty, size } from 'lodash'
import { UIButton } from 'components/ui/Button'
import { WebsocketActionsEnum } from 'types/index'
import { store } from 'store/index'
import { Chat } from 'types/chat'
import { ChatsDiv } from 'components/ui/MessageDiv'
import clsx from 'clsx'
import { showOpenChatModal } from 'managers/modal'
import { useGetSetting } from 'api/settings'
import { openNewChat } from 'api/chats'
import { showSuccessToast } from 'utils/toast'
import { socketSingleton } from 'services/ws'
import { useGetAreas } from 'api/areas'

export const ChatsList = () => {
  const { setting } = useGetSetting()
  const { t } = useTranslation()
  const chatsMap = useSelector(messagesSelectors.chats)
  const currentUserId = useSelector(settingsSelectors.userId)
  const areaId = useSelector(settingsSelectors.areaId)
  const { data: areas } = useGetAreas()
  const [loading, setLoading] = useState(false)
  const sortedChats = useMemo(() => {
    return chain(chatsMap).values().orderBy(['updatedAt'], ['desc']).value()
  }, [chatsMap])

  useEffect(() => {
    if (currentUserId) {
      socketSingleton.connectTo(String(currentUserId), (wsMessage) => {
        console.info({ wsMessage })
        if (wsMessage.action === WebsocketActionsEnum.NEW_MESSAGE) {
          store.dispatch(
            messagesActions.updateChat({
              lastMessage: wsMessage.body,
              _id: wsMessage.body.chatId,
            }),
          )
        }
        if (wsMessage.action === WebsocketActionsEnum.UPDATE_CHAT_PARTICIPANTS) {
          const updatedChat = wsMessage.body as Chat
          store.dispatch(messagesActions.setChat(updatedChat))
        }
      })
    }

    return () => {
      if (currentUserId) {
        socketSingleton.disconnectTo(String(currentUserId))
      }
    }
  }, [currentUserId])

  const openChatWithCurrentAreaId = async (areaId: string) => {
    try {
      setLoading(true)
      await openNewChat({ areaId })
      showSuccessToast(t('chatOpenedSuccess'))
    } finally {
      setLoading(false)
    }
  }

  const onClickOpenChat = async () => {
    if (areaId) {
      return await openChatWithCurrentAreaId(areaId)
    }
    if (size(areas) === 1) {
      const firstArea = areas[0]
      return await openChatWithCurrentAreaId(firstArea._id)
    }
    showOpenChatModal()
  }

  return (
    <>
      <ChatsDiv
        className={clsx(
          'tw-flex-grow tw-w-full tw-flex-col tw-self-start tw-overflow-y-auto tw-rounded-t-3xl tw-bg-primary-500 tw-p-2 tw-pt-2 tw-py-2',
        )}
        widgetColor={setting?.secondaryColor}
      >
        <div className={''}>
          {isEmpty(sortedChats) && <div>{t('emptyChats')}</div>}
          {sortedChats.map((chat) => (
            <ChatRow chat={chat} key={chat._id} />
          ))}
        </div>
      </ChatsDiv>
      <div className={clsx('tw-flex-row tw-items-center tw-w-full tw-px-3 tw-mt-1 tw-mb-4')}>
        <UIButton
          color={setting?.primaryColor}
          loading={loading}
          className={'tw-p-2 tw-rounded-2xl tw-w-full tw-text-white'}
          onClick={onClickOpenChat}
        >
          {t('openNewChat')}
        </UIButton>
      </div>
    </>
  )
}
